import * as React from "react";
import {
  Video,
  VideoContentStatusOptions,
  VideoContentTypeOptions
} from "@models/video-content";
import {Modal, Table, Button, Divider, Avatar, Tooltip} from "antd";
import {FormattedMessage, injectIntl, intlShape} from "react-intl";
import FormattedDateTime from "@components/custom/FormattedDateTime";
import Message from "@constants/message";
import {PaginationDefaultParams} from "../custom/Paginator";
import {NavLink} from "react-router-dom";
import {Icon} from 'antd';
import __t from '../../intl';

type Props = {
  data: Video[];
  loading: boolean;
  deleteFn: (item: Video) => void;
  intl: intlShape.isRequired;
  currentPage: number;
};

const VideosTable: React.FC<Props> = props => {
  const {data, loading, currentPage, deleteFn, intl} = props;
  const {formatMessage} = intl;
  const pageOffset = (currentPage - 1) * PaginationDefaultParams.count;
  
  const deleteVideo = (item: Video): void => {
    Modal.confirm({
      content: formatMessage({
        id: "app.general_delete_warning",
        defaultMessage: Message.Warning.Delete
      }),
      okText: formatMessage({
        id: "app.delete",
        defaultMessage: "Delete"
      }),
      cancelText: formatMessage({
        id: "app.cancel",
        defaultMessage: "Cancel"
      }),
      className: "delete-confirm-dialog",
      onOk: () => deleteFn(item)
    });
  };
  
  const columns = [
    {
      title: <FormattedMessage id="app.index" defaultMessage={`Index`}/>,
      width: 30,
      dataIndex: "id",
      key: "id",
      align: "right",
      render: (a, b, i) => <span>{i + 1 + pageOffset}</span>
    },
    {
      title: "",
      width: 30,
      align: "right",
      dataIndex: "poster_image",
      key: "poster_image",
      render: (a, record) => (
        <Avatar
          shape="square"
          size="default"
          src={record.poster_image ? record.poster_image.path : null}
        />
      )
    },
    {
      title: <FormattedMessage id="app.type" defaultMessage={`Type`}/>,
      dataIndex: "type",
      key: "type",
      width: 50,
      align: "right",
      render: type => {
        const obj = VideoContentTypeOptions.find(item => item.value == type);
        return obj ? obj.label : type;
      }
    },
    {
      title: <FormattedMessage id="app.episode" defaultMessage={`Episode`}/>,
      dataIndex: "episode",
      key: "episode",
      align: "right",
    },
    {
      title: <FormattedMessage id="app.title" defaultMessage={`Title`}/>,
      width: 200,
      align: "right",
      dataIndex: "title",
      key: "title",
    },
    {
      title: <FormattedMessage id="app.series" defaultMessage={`Series`}/>,
      dataIndex: "supper_title",
      key: "supper_title",
      align: "right",
    },
    {
      title: <FormattedMessage id="app.season" defaultMessage={`Season`}/>,
      dataIndex: "upper_title",
      key: "upper_title",
      align: "right",
    },
    {
      title: <FormattedMessage id="app.status" defaultMessage={`Status`}/>,
      dataIndex: "status",
      key: "status",
      width: 30,
      align: "center",
      render: status =>
        VideoContentStatusOptions.find(opt => opt.value == status)["label"]
    },
    {
      title: (
        <FormattedMessage
          id="app.publish_date"
          defaultMessage={`Publish Date`}
        />
      ),
      width: 120,
      align: "center",
      dataIndex: "published_at",
      key: "published_at",
      render: (text, record) => <FormattedDateTime date={text}/>
    },
    {
      title: (
        <FormattedMessage
          id="app.creation_date"
          defaultMessage={`Creation Date`}
        />
      ),
      align: "center",
      width: 120,
      dataIndex: "created_at",
      key: "created_at",
      render: (text, record) => <FormattedDateTime date={text}/>
    },
    {
      title: "",
      key: "actions",
      width: 150,
      align: "left",
      dataIndex: "actions",
      render: (text, record) => (
        <span>
           <Tooltip title={<FormattedMessage id="app.edit" defaultMessage={`Edit`}/>}>
             <NavLink to={`/video-contents/${record.id}/edit`}>
               <Button type="link" size={`small`}>
                 <Icon type="edit"/>
              </Button>
             </NavLink>
          </Tooltip>

          <Divider type="vertical" size={`small`}/>

         
          <Tooltip title={<FormattedMessage id="app.delete" defaultMessage={`Delete`}/>}>
             <Button
               type="link"
               className={`danger`}
               onClick={() => deleteVideo(record)}
             >
            <Icon type="delete"/>
            </Button>
          </Tooltip>
          
          <Divider type="vertical" size={`small`}/>
          
          <Tooltip title={<FormattedMessage id="app.copy" defaultMessage={`Copy`}/>}>
            <NavLink to={`/video-contents/create?ref=${record.id}`}>
              <Button type="link" size={`small`}>
                <Icon type="copy"/>
              </Button>
          </NavLink>
          </Tooltip>
          
          <Divider type="vertical" size={`small`}/>
          
          <Tooltip title={<FormattedMessage id="app.view_count_report" defaultMessage={`Report`}/>}>
            <NavLink to={`/video-contents/${record.id}/report`}>
              <Button type="link" size={`small`}>
                <Icon type="area-chart"/>
              </Button>
            </NavLink>
          </Tooltip>
          
        </span>
      )
    }
  ];
  
  return (
    <Table
      columns={columns}
      loading={loading}
      pagination={false}
      dataSource={data}
      rowKey="id"
    />
  );
};

export default injectIntl(VideosTable);
